import { HttpErrorResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, ParamMap } from '@angular/router'
import { BehaviorSubject, combineLatest, of, Subject, Subscription } from 'rxjs'
import { catchError, distinctUntilChanged, map, shareReplay, skip, switchMap, takeUntil, tap } from 'rxjs/operators'
import { PageMetaService } from '../../libs/page-meta/page-meta.service'
import { LOGGER_SERVICE } from '../../logger/logger.config'
import { LoggerService } from '../../logger/logger.interface'
import { ArticleDataService } from '../article-data.service'
import { ArticleListAnalyticsService } from './article-list-analytics.service'
import { ExperimentationService } from '../../shared/experimentation/experimentation.service'
import {
  FEAT_OFP_DATA_ARTICLE_LIST_ID,
  FEAT_OFP_DATA_ARTICLE_LIST_VARIATION
} from '../../shared/experimentation/experiment.model'

@Component({
  templateUrl: './article-list.component.html',
  styleUrls: ['./article-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArticleListComponent implements OnInit, OnDestroy {
  private readonly ngOnDestroy$ = new Subject<void>()

  private articlesLimit = new BehaviorSubject<number>(10)
  private alreadyFetchedNext = false

  private routeQueryParam$ = this.activatedRoute.queryParamMap.pipe(
    distinctUntilChanged((x: ParamMap, y: ParamMap) => x.get('nav_id') === y.get('nav_id')),
    shareReplay(1)
  )

  private navId$ = this.routeQueryParam$.pipe(map(routeQueryParam => routeQueryParam.get('nav_id')))

  /**
   * Subscription to send a page call any time the navId changes, but the /articles route stays the same.
   * The skip(1) is necessary to prevent duplicate page calls from getting made when the component first loads
   * on a page that includes a navId, because a call is also made in ngOnInit when the component first loads.
   */
  private sendPageCallOnNavIdChange$: Subscription = this.navId$
    .pipe(skip(1), takeUntil(this.ngOnDestroy$))
    .subscribe(_ => {
      // sends a page call anytime the navId changes over the component life cycle
      this.analytics.sendPageCall()
    })

  // OFP-1150 Feature flag for list of OFP articles
  private ofpFeatureFlag$ = this.experimentationService.isInVariation(
    FEAT_OFP_DATA_ARTICLE_LIST_ID,
    FEAT_OFP_DATA_ARTICLE_LIST_VARIATION,
    false
  )

  /**
   * This just helps with type safety be isolating the any from Route Data.
   * TODO: Intro page param, and re-fetch articles based on both nav_id + page query param changes.
   * @see {@link https://flocasts.atlassian.net/browse/CXP-3115}
   */
  public readonly articles$ = combineLatest([this.routeQueryParam$, this.articlesLimit, this.ofpFeatureFlag$]).pipe(
    switchMap(([queryParams, articlesLimit, isOfp]) => {
      return this.articleApi.getArticles(articlesLimit, isOfp, queryParams.get('nav_id') ?? undefined).pipe(
        map(floResponse => floResponse.data),
        // TODO ofp will not have the analytics data we need
        map(articles => this.analytics.addListAnalytics(articles)),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status >= 500) {
            this.log.error('An error occurred while fetching article data.', {
              error
            })
          }
          return of([])
        }),
        tap(() => (this.alreadyFetchedNext = false))
      )
    })
  )

  // prettier-ignore
  constructor(
    @Inject(LOGGER_SERVICE) private readonly log: LoggerService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly analytics: ArticleListAnalyticsService,
    private readonly articleApi: ArticleDataService,
    private readonly pageMeta: PageMetaService,
    private readonly experimentationService: ExperimentationService,
  ) {
    this.analytics.setPageMeta()
  }

  /**
   * Update articlesLimit so that articles$ observable updates with results from new response.
   * TODO: Update page param instead of limit here.
   * @see {@link https://flocasts.atlassian.net/browse/CXP-3115}
   */
  public showMore(): void {
    if (!this.alreadyFetchedNext) {
      this.alreadyFetchedNext = true
      this.articlesLimit.next(this.articlesLimit.getValue() + 10)
    }
  }

  public ngOnInit() {
    this.analytics.sendPageCall()
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()

    this.pageMeta.clearMeta()
  }
}
